import { AfterViewInit, Component, EventEmitter, Input, OnDestroy, Output, forwardRef, input } from '@angular/core';
import {
  ControlValueAccessor,
  NG_VALUE_ACCESSOR,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { TriggerRuleForm } from '@anduin/api';
import { LaunchDarklyFlag, LaunchDarklyState } from '@anduin/auth';
import { Store } from '@ngxs/store';
import { Subject } from 'rxjs';
import { takeUntil, tap } from 'rxjs/operators';

@Component({
  selector: 'app-trigger-builder',
  templateUrl: './trigger-builder.component.html',
  styleUrls: ['./trigger-builder.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => TriggerBuilderComponent),
      multi: true,
    },
  ],
  standalone: false,
})
export class TriggerBuilderComponent implements AfterViewInit, OnDestroy, ControlValueAccessor {
  OPERATOR_OPTIONS = [
    {
      label: 'was more than',
      value: '>',
    },
    {
      label: 'is less than',
      value: '<=',
    },
  ];

  hasChanged: boolean;

  @Output()
  hasChange = new EventEmitter<boolean>();

  @Output()
  removeTrigger = new EventEmitter<void>();

  @Output()
  updated = new EventEmitter<void>();

  @Input()
  order: number;

  @Input()
  index: number;

  isOnlyTrigger = input<boolean>(undefined);
  isDisabled = false;

  initValue: TriggerRuleForm;

  onChange = (trigger: TriggerRuleForm) => {};
  onTouch = () => {};

  unsubscribe$: Subject<void> = new Subject<void>();

  builderForm: UntypedFormGroup = this.fb.group({
    id: this.fb.control(null),
    priority: this.fb.control(null),
    comparator: this.fb.control('days_relative_to_due_date'),
    comparatorOperator: this.fb.control(this.OPERATOR_OPTIONS[0].value, { validators: Validators.required }),
    comparatorValue: this.fb.control(null, { validators: [Validators.required, Validators.min(0)], updateOn: 'blur' }),

    // this property help with sorting pending rows, essentially ignoring updated values until row is submitted
    previousValue: this.fb.control(null),
  });

  get comparatorOperator(): UntypedFormControl {
    return this.builderForm.controls['comparatorOperator'] as UntypedFormControl;
  }

  get comparatorValueControl(): UntypedFormControl {
    return this.builderForm.controls['comparatorValue'] as UntypedFormControl;
  }

  constructor(
    private readonly fb: UntypedFormBuilder,
    private readonly store: Store,
  ) {}

  getNewTriggerId(): number {
    const isCollectionsV2Enabled = this.store.selectSnapshot(LaunchDarklyState.getFeatureFlag(LaunchDarklyFlag.ENABLE_COLLECTIONS_V2));
    return isCollectionsV2Enabled ? undefined : this.index;
  }

  writeValue(trigger: TriggerRuleForm): void {
    if (trigger) {
      this.builderForm.patchValue({
        id: trigger.id ?? this.getNewTriggerId(),
        priority: trigger.priority,
        comparatorOperator: trigger.comparatorOperator || this.OPERATOR_OPTIONS[0],
        comparatorValue: trigger.comparatorValue,
        previousValue: trigger.previousValue,
      });
      this.initValue = { ...trigger, id: trigger.id ?? this.getNewTriggerId() };
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouch = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
    if (isDisabled) {
      this.builderForm.disable();
    } else {
      this.builderForm.enable();
    }
  }

  ngAfterViewInit(): void {
    this.builderForm.valueChanges
      .pipe(
        tap((val: TriggerRuleForm) => {
          this.hasChange.emit(this.builderForm.dirty);
          this.onChange(val);
        }),
        takeUntil(this.unsubscribe$),
      )
      .subscribe();
  }

  revertChanges(): void {
    this.builderForm.reset({
      id: this.initValue.id ?? this.getNewTriggerId(),
      comparator: this.builderForm.value.comparator,
      comparatorOperator: this.initValue.comparatorOperator || this.OPERATOR_OPTIONS[0],
      comparatorValue: this.initValue.comparatorValue,
      previousValue: this.initValue.previousValue,
      priority: this.initValue.priority,
    });
  }

  save(): void {
    this.builderForm.markAllAsTouched();
    if (!this.builderForm.valid) {
      return;
    }

    const currentValues = {
      ...this.builderForm.value,
      previousValue: {
        comparatorOperator: this.builderForm.value.comparatorOperator,
        comparatorValue: this.builderForm.value.comparatorValue,
      },
    };
    this.initValue = currentValues;
    this.builderForm.reset(currentValues);
    this.updated.emit();
  }

  remove(): void {
    this.removeTrigger.next();
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }
}
